import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["activeTrials", "expiredTrials", "isMultiTrial", "upgradeData", "upgradeData", "onSuccess", "onClose", "onTrialStartSuccess", "shouldHideSurvey"];
import { useCallback, useEffect, useRef, useState } from 'react';
import { trialIdsWithTrialGuide } from 'self-service-api/constants/Trials';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import ActivationDuplicateErrorModal from '../../common/components/activation/ActivationDuplicateErrorModal';
import ActivationErrorModal from '../../common/components/activation/ActivationErrorModal';
import { GLOBAL_HOME_NEXT_STEP_APINAMES } from '../../common/constants/trials';
import { track } from '../../common/eventTracking/tracker';
import { navigateTo } from '../../common/navigation/url';
import { TRIAL } from '../../purchaseMotions/PurchaseMotionTypes';
import { getGlobalHomeUrl, getTrialGuideUrl, getTrialGuideUrlFromApiName, refresh } from '../../utils/links';
import { trackPurchaseMotionFailure, trackTrialActivation } from '../../utils/metricUtils';
import TrialActivationSuccessContainer from './TrialActivationSuccessContainer';
import { activateTrial } from './activateTrial';
import { jsx as _jsx } from "react/jsx-runtime";
const statuses = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
const HIDE_SUBTITLE_SCREENS = ['products-plans', 'service-hub-paywall'];
const TrialActivationModalContainer = props => {
  const [trialActivationRequestStatus, setTrialActivationRequestStatus] = useState(statuses.LOADING);
  const [showTrialLoadingState, setShowTrialLoadingState] = useState(!!props.shouldHideSurvey);
  const [error, setError] = useState(null);
  const isMounted = useRef(false);
  const {
      activeTrials,
      expiredTrials,
      isMultiTrial,
      upgradeData,
      upgradeData: {
        app,
        screen,
        uniqueId,
        upgradeProduct
      },
      onSuccess,
      onClose,
      onTrialStartSuccess,
      shouldHideSurvey
    } = props,
    successModalPassthroughProps = _objectWithoutPropertiesLoose(props, _excluded);
  const apiName = upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct];
  const showSubtitle = !HIDE_SUBTITLE_SCREENS.includes(screen);
  const defaultOnSuccess = useCallback(() => {
    track('trialModalInteraction', Object.assign({
      action: 'clicked continue button'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    if (onSuccess) {
      onSuccess();
    } else {
      var _window$opener;
      const {
        trialId
      } = upgradeData;
      if (typeof window !== 'undefined' && (_window$opener = window.opener) !== null && _window$opener !== void 0 && _window$opener.location.href) {
        navigateTo(window.opener.location.href);
      } else if (trialId && trialIdsWithTrialGuide.includes(trialId)) {
        const source = `${app}-${screen}-${uniqueId}`;
        navigateTo(apiName ? getTrialGuideUrlFromApiName(apiName, source) : getTrialGuideUrl(upgradeProduct, source));
      } else if (GLOBAL_HOME_NEXT_STEP_APINAMES.includes(apiName)) {
        track('trialActivationRedirected', Object.assign({
          redirectTarget: 'global-home'
        }, getUpgradeDataTrackingProperties(upgradeData), {
          trialId: upgradeData.trialId,
          duration: upgradeData.duration
        }));
        navigateTo(getGlobalHomeUrl());
      } else {
        refresh();
      }
    }
  }, [onSuccess, app, screen, uniqueId, upgradeData, upgradeProduct, apiName]);
  useEffect(() => {
    isMounted.current = true;
    track('trialModalInteraction', Object.assign({
      action: 'opened modal container'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    activateTrial({
      upgradeData,
      isMultiTrial,
      activeTrials,
      expiredTrials,
      onTrialStartSuccess
    }).then(() => {
      if (isMounted.current) {
        setTrialActivationRequestStatus(statuses.SUCCESS);
        setShowTrialLoadingState(false);
        trackTrialActivation(upgradeData.app, upgradeData.upgradeProduct);
      }
    }).catch(err => {
      if (isMounted.current) {
        trackPurchaseMotionFailure({
          uniqueId: upgradeData.uniqueId,
          apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
          purchaseMotion: TRIAL
        });
        setTrialActivationRequestStatus(statuses.ERROR);
        setError(err);
      }
    });
    return () => {
      isMounted.current = false;
    };
  }, [activeTrials, expiredTrials, isMultiTrial, onTrialStartSuccess, upgradeData]);
  switch (trialActivationRequestStatus) {
    case statuses.LOADING:
      return null;
    case statuses.ERROR:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation error modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      if (error && error.responseJSON) {
        const activateError = error.responseJSON;
        if (activateError.message && activateError.message.match(/^Cannot start this trial because it is already active on this hub/)) {
          return /*#__PURE__*/_jsx(ActivationDuplicateErrorModal, {
            onSuccess: defaultOnSuccess,
            upgradeData: upgradeData
          });
        }
      }
      return /*#__PURE__*/_jsx(ActivationErrorModal, {});
    case statuses.SUCCESS:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation success modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      return /*#__PURE__*/_jsx(TrialActivationSuccessContainer, Object.assign({
        onSuccess: defaultOnSuccess,
        onClose: onClose,
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        isMultiTrial: isMultiTrial,
        upgradeData: upgradeData,
        showTrialLoadingState: showTrialLoadingState,
        showSubtitle: showSubtitle,
        shouldHideSurvey: shouldHideSurvey
      }, successModalPassthroughProps));
    default:
      return null;
  }
};
export default TrialActivationModalContainer;